import { useEffect, useMemo, useState } from 'react';
import type { PrayerDay, IslamicEvent } from '../lib/types';
import { readJson, writeJson } from '../lib/storage';
import {
  firstDayOfMonthISO,
  formatTime,
  monthLabel,
  monthYearFromISO,
  normalizeISODate,
  parseMonthInput,
  todayISO,
  monthInputValue
} from '../lib/time';
import { prayerMonth } from '../lib/api';
import { fetchWithCache } from '../lib/appCache';
import { TimetableTable } from '../components/TimetableTable';
import { CityPicker } from '../components/CityPicker';
import { getEffectiveHijriAdjustment } from '../lib/hijriRegional';
import { CALC_METHODS } from '../lib/constants';
import { applyHijriAdjustmentToPrayerDays, deriveUpcomingIslamicEvents } from '../lib/hijriEngine';
import { useLocationState } from '../lib/useLocationState';
import { useSettingsState } from '../lib/useSettingsState';
import { useLocale, hijriLabel, hijriMonthName, eventName, calcMethodLabel, WEEKDAYS_SHORT, WEEKDAYS_LONG, bcp47, fmt } from '../lib/i18n';

/* ── Small icons ───────────────────────────────────────── */

function CaretIcon({ open }: { open: boolean }) {
  return (
    <svg width="11" height="11" viewBox="0 0 10 10" fill="none" style={{ transition: 'transform .2s ease', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', opacity: 0.7 }}>
      <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LocationIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <path d="M8 14s5-4.5 5-8.5A5 5 0 1 0 3 5.5C3 9.5 8 14 8 14z" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="8" cy="5.5" r="1.6" fill="currentColor" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <rect x="3.5" y="5" width="17" height="15" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M3.5 10h17" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="12" cy="15" r="1.3" fill="currentColor" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M14 8a6 6 0 1 1-1.76-4.24M14 3v3h-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.3" stroke="currentColor" strokeWidth="1.3" />
      <path d="M8 7.2v4M8 5.2v.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function TimetableIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="3" width="12" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M2 6h12M5 2v3M11 2v3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

const PRAYER_ORDER: Array<{ k: keyof PrayerDay['timings']; meta?: boolean }> = [
  { k: 'fajr' },
  { k: 'sunrise', meta: true },
  { k: 'dhuhr' },
  { k: 'asr' },
  { k: 'maghrib' },
  { k: 'isha' },
];

/* ── Helpers ────────────────────────────────────────────── */

function hijriMonthShort(name: string): string {
  const map: Record<string, string> = {
    'Muharram': 'MH', 'Safar': 'SF', "Rabi' al-Awwal": 'R1', "Rabi' al-Thani": 'R2',
    'Jumada al-Ula': 'J1', 'Jumada al-Thani': 'J2', 'Rajab': 'RJ', "Sha'ban": 'SH',
    'Ramadan': 'RM', 'Shawwal': 'SW', "Dhu al-Qi'dah": 'DQ', 'Dhu al-Hijjah': 'DH',
  };
  return map[name] ?? name.substring(0, 2).toUpperCase();
}

function eventKindForEvent(e: IslamicEvent): 'eid' | 'major' | 'minor' {
  const n = e.name.toLowerCase();
  if (n.includes('eid')) return 'eid';
  if (e.importanceRank <= 3 || n.includes('hajj') || n.includes('arafah') || n.includes('laylat') || n.includes('new year')) return 'major';
  return 'minor';
}

function daysUntilISO(iso: string): number {
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000));
}

/* ── Top Strip ─────────────────────────────────────────── */

function CalTopStrip(props: {
  location: import('../lib/types').Location | null;
  monthLabel: string;
  hijriBand: string;
  settings: import('../lib/types').Settings;
  year: number;
  month: number;
  onSettingsChange: (s: import('../lib/types').Settings) => void;
  onLocationChange: (loc: import('../lib/types').Location) => void;
  onMonthChange: (value: string) => void;
  onCurrentMonth: () => void;
  onRefresh: () => void;
  busy: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const { location, settings } = props;
  const { t, locale } = useLocale();

  return (
    <div className="ppTopStrip">
      <button className="ppTopStripBtn" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}>
        <div className="ppTopStripLeft">
          <span className="ppLocPin" aria-hidden="true"><LocationIcon /></span>
          <span className="ppLoc">{location?.city ?? t.selectCityShort}</span>
          <span className="ppLocSep">·</span>
          <span className="ppHijri">{props.monthLabel}</span>
          <span className="ppLocSep">·</span>
          <span className="ppMuted ppSmall">{props.hijriBand}</span>
        </div>
        <CaretIcon open={expanded} />
      </button>

      {expanded && (
        <div className="ppTopStripPanel">
          <div className="ppTopStripPanelRow">
            <label className="ppFld">
              <span className="ppFldLabel">{t.monthLabel}</span>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <button className="btn" onClick={() => {
                  const prev = props.month === 1 ? 12 : props.month - 1;
                  const yr = props.month === 1 ? props.year - 1 : props.year;
                  props.onMonthChange(`${yr}-${String(prev).padStart(2, '0')}`);
                }}>‹</button>
                <input className="input" type="month" value={monthInputValue(props.year, props.month)} onChange={(e) => props.onMonthChange(e.target.value)} style={{ width: 160 }} />
                <button className="btn" onClick={() => {
                  const next = props.month === 12 ? 1 : props.month + 1;
                  const yr = props.month === 12 ? props.year + 1 : props.year;
                  props.onMonthChange(`${yr}-${String(next).padStart(2, '0')}`);
                }}>›</button>
                <button className="btn" onClick={props.onCurrentMonth}>{t.currentMonth}</button>
              </div>
            </label>
            <label className="ppFld">
              <span className="ppFldLabel">{t.locationLabel}</span>
              <CityPicker value={location} onChange={props.onLocationChange} />
            </label>
          </div>
          <div className="ppTopStripPanelRow">
            <label className="ppFld">
              <span className="ppFldLabel">{t.calculation}</span>
              <select className="input" value={String(settings.methodId)} onChange={(e) => props.onSettingsChange({ ...settings, methodId: Number(e.target.value) })}>
                {CALC_METHODS.map((m) => <option key={m.id} value={String(m.id)}>{calcMethodLabel(locale, m.id, m.label)}</option>)}
              </select>
            </label>
            <label className="ppFld">
              <span className="ppFldLabel">{t.hijriAdjustment}</span>
              <div className="ppSeg">
                <button className={`ppSegBtn ${settings.hijriAdjustment === -1 ? 'on' : ''}`} onClick={() => props.onSettingsChange({ ...settings, hijriAdjustmentMode: 'manual', hijriAdjustment: -1 })}>−1</button>
                <button className={`ppSegBtn ${settings.hijriAdjustment === 0 ? 'on' : ''}`} onClick={() => props.onSettingsChange({ ...settings, hijriAdjustmentMode: 'manual', hijriAdjustment: 0 })}>0</button>
                <button className={`ppSegBtn ${settings.hijriAdjustment === 1 ? 'on' : ''}`} onClick={() => props.onSettingsChange({ ...settings, hijriAdjustmentMode: 'manual', hijriAdjustment: 1 })}>+1</button>
              </div>
            </label>
          </div>
          <div className="ppTopStripFootnote">
            <span>{t.sourceAladhan}</span>
            <span>·</span>
            <span>{t.timezoneLabel}: {location?.timezone ?? '—'}</span>
            <span style={{ flex: 1 }} />
            <button className="ppLinklike" onClick={props.onRefresh} disabled={props.busy}>{props.busy ? t.refreshing : t.refresh}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Upcoming events ribbon ────────────────────────────── */

function UpcomingRibbon(props: { events: IslamicEvent[] }) {
  const { t, locale } = useLocale();
  const [open, setOpen] = useState(false);
  if (!props.events.length) return null;
  const next = props.events[0];
  const kind = eventKindForEvent(next);
  const d = daysUntilISO(next.approxGregorianRange.startISO);

  return (
    <div className="ppEvt">
      <button className="ppEvtHeader" onClick={() => setOpen((v) => !v)}>
        <span className={`calEvtDot ${kind}`} style={{ width: 8, height: 8 }} />
        <span className="ppEvtMain">
          <strong>{eventName(locale, next.hijriDate.month, next.hijriDate.day, next.name)}</strong>
          <span className="ppMuted"> · {next.hijriDate.day}/{next.hijriDate.month}</span>
          <span className="ppMuted"> · {fmt(t.inDays, { n: d })}</span>
        </span>
        <span className="ppEvtMore">
          {open ? t.hide : `+${props.events.length - 1} ${t.moreThisMonth}`} <CaretIcon open={open} />
        </span>
      </button>
      {open && (
        <div className="ppEvtList">
          {props.events.slice(1).map((e) => {
            const k = eventKindForEvent(e);
            return (
              <div key={e.name + e.approxGregorianRange.startISO} className="ppEvtRow">
                <span className={`calEvtDot ${k}`} style={{ width: 6, height: 6 }} />
                <span><strong>{eventName(locale, e.hijriDate.month, e.hijriDate.day, e.name)}</strong> <span className="ppMuted">· {e.hijriDate.day}/{e.hijriDate.month}</span></span>
                <span className="ppMuted ppSmall">{e.approxGregorianRange.startISO}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Calendar grid ─────────────────────────────────────── */

type CalCellData = {
  type: 'empty';
  key: string;
} | {
  type: 'day';
  key: string;
  gregDay: number;
  iso: string;
  day: PrayerDay;
  hijriDay: number;
  hijriMonthName: string;
  hijriMonthShort: string;
  isToday: boolean;
  isSelected: boolean;
  isFirstOfHijriMonth: boolean;
  events: Array<{ event: IslamicEvent; kind: 'eid' | 'major' | 'minor' }>;
};

function CalendarGrid(props: {
  days: PrayerDay[];
  events: IslamicEvent[];
  selectedISO: string;
  todayISO: string;
  onSelect: (iso: string) => void;
  compact?: boolean;
}) {
  const { t, locale } = useLocale();
  const { days, events, selectedISO, todayISO: today } = props;

  const eventsByDate = useMemo(() => {
    const m = new Map<string, Array<{ event: IslamicEvent; kind: 'eid' | 'major' | 'minor' }>>();
    for (const e of events) {
      const iso = e.approxGregorianRange.startISO;
      const arr = m.get(iso) ?? [];
      arr.push({ event: e, kind: eventKindForEvent(e) });
      m.set(iso, arr);
    }
    return m;
  }, [events]);

  const { cells, monthLabel: mLabel, hijriBand } = useMemo(() => {
    if (!days.length) return { cells: [] as CalCellData[], monthLabel: '', hijriBand: '' };

    const firstISO = days[0].dateGregorianISO;
    const [y, m] = firstISO.split('-').map(Number);
    const firstWeekday = new Date(y, m - 1, 1).getDay();
    const pad = (firstWeekday + 6) % 7;

    const result: CalCellData[] = [];
    for (let i = 0; i < pad; i++) result.push({ type: 'empty', key: `e-${i}` });

    const hijriMonths = new Set<string>();
    let prevHijriMonth = '';

    for (const d of days) {
      const gregDay = Number(d.dateGregorianISO.slice(-2));
      const hName = hijriMonthName(locale, d.dateHijri.month);
      hijriMonths.add(`${hName} ${d.dateHijri.year}`);
      const isFirst = hName !== prevHijriMonth && prevHijriMonth !== '';
      prevHijriMonth = hName;

      result.push({
        type: 'day',
        key: d.dateGregorianISO,
        gregDay,
        iso: d.dateGregorianISO,
        day: d,
        hijriDay: d.dateHijri.day,
        hijriMonthName: hName,
        hijriMonthShort: locale === 'en' ? hijriMonthShort(d.dateHijri.monthName) : hName.slice(0, 3),
        isToday: d.dateGregorianISO === today,
        isSelected: d.dateGregorianISO === selectedISO,
        isFirstOfHijriMonth: isFirst,
        events: eventsByDate.get(d.dateGregorianISO) ?? [],
      });
    }

    const totalCells = Math.ceil(result.length / 7) * 7;
    while (result.length < totalCells) result.push({ type: 'empty', key: `et-${result.length}` });

    const months = new Intl.DateTimeFormat(bcp47(locale), { month: 'long', year: 'numeric' }).format(new Date(y, m - 1));
    return { cells: result, monthLabel: months, hijriBand: [...hijriMonths].join(' – ') };
  }, [days, today, selectedISO, eventsByDate, locale]);

  if (!cells.length) return null;

  return (
    <div className="calCard">
      <div className="calCardTop">
        <div className="calCardTitle">
          <span className="calCardMonth">{mLabel}</span>
          <span className="calCardHijri">{hijriBand}</span>
        </div>
        <div className="calLegend">
          <span className="legendItem"><span className="legendDot today" /> {t.legendToday}</span>
          <span className="legendItem"><span className="legendDot major" /> {t.legendMajor}</span>
          <span className="legendItem"><span className="legendDot eid" /> {t.legendEid}</span>
        </div>
      </div>

      <div className="calWeekHead">
        {WEEKDAYS_SHORT[locale].map((w) => <div key={w} className="calWeekHeadCell">{w}</div>)}
      </div>

      <div className={`calGrid ${props.compact ? 'compact' : ''}`}>
        {cells.map((c) => {
          if (c.type === 'empty') return <div key={c.key} className="calCell empty" />;
          const mainEvt = c.events[0];
          const cls = [
            'calCell',
            c.isToday && 'today',
            c.isSelected && 'selected',
            c.events.length && `hasEvent kind-${mainEvt.kind}`,
            c.isFirstOfHijriMonth && 'hijriBoundary',
          ].filter(Boolean).join(' ');

          return (
            <button key={c.key} className={cls} onClick={() => props.onSelect(c.iso)}>
              <div className="calCellTopRow">
                <span className="calG">{c.gregDay}</span>
                <span className="calH">
                  {c.hijriDay}
                  <span className="calHm">{c.hijriMonthShort}</span>
                </span>
              </div>
              {c.events.length > 0 ? (
                <div className="calEvt">
                  <span className={`calEvtDot ${mainEvt.kind}`} />
                  <span className="calEvtName">{eventName(locale, mainEvt.event.hijriDate.month, mainEvt.event.hijriDate.day, mainEvt.event.name)}{c.events.length > 1 ? ` +${c.events.length - 1}` : ''}</span>
                </div>
              ) : c.isFirstOfHijriMonth ? (
                <div className="calBoundary">
                  <span className="calBoundaryArrow">→</span>
                  <span>{c.hijriMonthName}</span>
                </div>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Selected-day panel ────────────────────────────────── */

function SelectedDayPanel(props: {
  day: PrayerDay;
  events: Array<{ event: IslamicEvent; kind: string }>;
  isToday: boolean;
  timeFormat: '24h' | '12h';
}) {
  const { t, locale, prayerName } = useLocale();
  const { day, events, isToday } = props;
  const gregDay = Number(day.dateGregorianISO.slice(-2));
  const [y, m] = day.dateGregorianISO.split('-').map(Number);
  const dayOfWeek = new Date(y, m - 1, gregDay).getDay();
  const dayName = WEEKDAYS_LONG[locale][dayOfWeek];
  const monthName = new Intl.DateTimeFormat(bcp47(locale), { month: 'long' }).format(new Date(y, m - 1));
  const isFriday = dayOfWeek === 5;

  return (
    <div className="dayPanel">
      <div className="dayPanelHead">
        <div>
          <div className="dayPanelEyebrow">{isToday ? t.todayEyebrow : t.selectedEyebrow}</div>
          <div className="dayPanelTitle">{dayName}{locale === 'en' ? ',' : '،'} {gregDay} {monthName}</div>
          <div className="dayPanelHijri">{hijriLabel(locale, day.dateHijri)}</div>
        </div>
        {isFriday && <div className="dayPanelChip">{t.jumuah}</div>}
      </div>

      {events.length > 0 && (
        <div className="dayPanelEvents">
          {events.map((e, i) => (
            <div key={i} className={`dayPanelEvent kind-${e.kind}`}>
              <span className={`calEvtDot ${e.kind}`} style={{ marginTop: 6, width: 8, height: 8 }} />
              <div>
                <div className="dayPanelEvtName">{eventName(locale, e.event.hijriDate.month, e.event.hijriDate.day, e.event.name)}</div>
                <div className="dayPanelEvtHijri ppMuted">{e.event.hijriDate.day}/{e.event.hijriDate.month}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="dayPanelTimes">
        {PRAYER_ORDER.map((p) => (
          <div key={p.k} className={`dpt ${p.meta ? 'meta' : ''}`}>
            <span className="dptLabel">{prayerName(p.k as string)}</span>
            <span className="dptTime">{formatTime(day.timings[p.k], props.timeFormat)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Full month timetable (collapsible) ────────────────── */

function FullMonthTimetable(props: { days: PrayerDay[]; timeFormat: '24h' | '12h'; title: string }) {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  if (!props.days.length) return null;

  return (
    <div className="ppTt">
      <button className="ppTtHeader" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <div className="ppTtHeaderLeft">
          <span className="ppTtIcon" aria-hidden="true"><TimetableIcon /></span>
          <span>{t.fullMonthTimetable}</span>
          <span className="ppMuted ppSmall">{fmt(t.allDaysSub, { n: props.days.length })}</span>
        </div>
        <CaretIcon open={open} />
      </button>
      {open && (
        <div className="ppTtBody">
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            <TimetableTable days={props.days} timeFormat={props.timeFormat} title={props.title} />
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Footer ────────────────────────────────────────────── */

function CalFooterNote() {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  return (
    <div className="ppFtr">
      <button className="ppFtrBtn" onClick={() => setOpen((v) => !v)}>
        <span className="ppFtrInfo" aria-hidden="true"><InfoIcon /></span>
        {t.aboutTheseDates}
        <CaretIcon open={open} />
      </button>
      {open && (
        <div className="ppFtrBody ppMuted">
          {t.calFooterBody}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   Main page
   ══════════════════════════════════════════════════════════ */

export function CalendarPage() {
  const { t, locale } = useLocale();
  const { location, setLocation } = useLocationState();
  const { settings, setSettings } = useSettingsState();
  const [daysRaw, setDaysRaw] = useState<PrayerDay[]>(() => readJson<PrayerDay[]>('cached_calendar_month', []));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const zonedToday = useMemo(() => todayISO(location?.timezone), [location?.timezone]);
  const [selectedMonthDate, setSelectedMonthDate] = useState<string>(() => readJson<string>('calendar_active_date', todayISO()));
  const activeMonthDate = useMemo(() => normalizeISODate(selectedMonthDate, zonedToday), [selectedMonthDate, zonedToday]);
  const { year, month } = useMemo(() => monthYearFromISO(activeMonthDate), [activeMonthDate]);
  const effectiveHijriAdjustment = useMemo(() => getEffectiveHijriAdjustment(settings, location), [settings, location]);

  const [selectedDayISO, setSelectedDayISO] = useState(zonedToday);

  useEffect(() => { writeJson('calendar_active_date', activeMonthDate); }, [activeMonthDate]);

  const days = useMemo(() => applyHijriAdjustmentToPrayerDays(daysRaw, effectiveHijriAdjustment), [daysRaw, effectiveHijriAdjustment]);
  const events = useMemo<IslamicEvent[]>(() => deriveUpcomingIslamicEvents(firstDayOfMonthISO(year, month), effectiveHijriAdjustment, 12), [year, month, effectiveHijriAdjustment]);
  const title = useMemo(() => monthLabel(year, month, location?.timezone, locale), [year, month, location?.timezone, locale]);

  const eventsByDate = useMemo(() => {
    const m = new Map<string, Array<{ event: IslamicEvent; kind: 'eid' | 'major' | 'minor' }>>();
    for (const e of events) {
      const iso = e.approxGregorianRange.startISO;
      const arr = m.get(iso) ?? [];
      arr.push({ event: e, kind: eventKindForEvent(e) });
      m.set(iso, arr);
    }
    return m;
  }, [events]);

  const selectedDay = useMemo(() => days.find((d) => d.dateGregorianISO === selectedDayISO) ?? null, [days, selectedDayISO]);
  const selectedDayEvents = useMemo(() => eventsByDate.get(selectedDayISO) ?? [], [eventsByDate, selectedDayISO]);

  const hijriBand = useMemo(() => {
    if (!days.length) return '';
    const months = new Set<string>();
    for (const d of days) months.add(`${hijriMonthName(locale, d.dateHijri.month)} ${d.dateHijri.year}`);
    return [...months].join(' – ');
  }, [days, locale]);

  async function refresh() {
    if (!location || !location.city || !location.country) return;
    setBusy(true);
    setError(null);
    try {
      const monthKey = `month:v3:raw|${year}-${month}|${location.city}|${location.country}|m=${settings.methodId}|md=${settings.madhhab}|tz=${location.timezone}`;
      const monthRes = await fetchWithCache(
        monthKey,
        () => prayerMonth(location, month, year, { ...settings, hijriAdjustmentMode: 'manual', hijriAdjustment: 0 })
          .then((d) => ({ ok: true as const, data: d.days }))
          .catch((e: unknown) => ({ ok: false as const, error: e instanceof Error ? e.message : 'Failed' })),
        { maxAgeMs: 6 * 60 * 60 * 1000 }
      );
      if (monthRes.result.ok) {
        setDaysRaw(monthRes.result.data);
        writeJson('cached_calendar_month', monthRes.result.data);
      } else {
        setError(monthRes.result.error);
      }
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!location || !location.city || !location.country) return;
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.city, location?.country, location?.timezone, settings.methodId, settings.madhhab, year, month]);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 860;

  return (
    <div className="calPage" style={{ maxWidth: 1100, margin: '0 auto', padding: '18px 22px 22px' }}>
      <div className="ppInner">
        {/* Header */}
        <div className="ppHeader">
          <div className="brand">
            <span className="brandMark" aria-hidden="true"><CalendarIcon /></span>
            <span className="brandName">{t.brandCalendar}</span>
          </div>
          <div className="row" style={{ gap: 6 }}>
            <button className="btn" style={{ padding: '8px 9px', borderRadius: 12 }} onClick={() => void refresh()} disabled={busy} title={t.refresh}>
              <RefreshIcon />
            </button>
          </div>
        </div>

        {/* Top strip */}
        <CalTopStrip
          location={location}
          monthLabel={title}
          hijriBand={hijriBand}
          settings={settings}
          year={year}
          month={month}
          onSettingsChange={setSettings}
          onLocationChange={setLocation}
          onMonthChange={(value) => {
            const parsed = parseMonthInput(value);
            if (!parsed) return;
            setSelectedMonthDate(firstDayOfMonthISO(parsed.year, parsed.month));
          }}
          onCurrentMonth={() => setSelectedMonthDate(zonedToday)}
          onRefresh={() => void refresh()}
          busy={busy}
        />

        {error && (
          <div className="card" style={{ padding: 14, borderColor: 'var(--danger-border)' }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{t.couldntRefresh}</div>
            <div className="ppMuted" style={{ fontSize: 13 }}>{error}</div>
          </div>
        )}

        {/* Events ribbon */}
        <UpcomingRibbon events={events} />

        {/* Main: grid + side panel */}
        {days.length > 0 ? (
          <div className={`calMain ${isMobile ? 'stack' : ''}`}>
            <div className="calMainGrid">
              <CalendarGrid
                days={days}
                events={events}
                selectedISO={selectedDayISO}
                todayISO={zonedToday}
                onSelect={setSelectedDayISO}
                compact={isMobile}
              />
            </div>
            <div className="calMainSide">
              {selectedDay && (
                <SelectedDayPanel
                  day={selectedDay}
                  events={selectedDayEvents}
                  isToday={selectedDayISO === zonedToday}
                  timeFormat={settings.timeFormat}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="card" style={{ padding: 14 }}>
            <div className="skeleton" style={{ height: 300, width: '100%' }} />
          </div>
        )}

        {/* Full month timetable */}
        <FullMonthTimetable days={days} timeFormat={settings.timeFormat} title={title} />

        {/* Footer */}
        <CalFooterNote />
      </div>
    </div>
  );
}
